import { API_AUTH_TOKEN } from "@iwoca/lapi-client/lendingAPI";

import logger from "../../utils/logger";
import { getAuthFlow } from "../auth-service-client";

/**
 * Custom fetch function for email availability that includes authorization header
 * This is needed because the generated lapi-client fetchers don't include the auth token
 */
async function getEmailAvailability(
  email: string,
): Promise<{ data: { available: boolean } }> {
  const encodedEmail = encodeURIComponent(email);
  const response = await fetch(
    `/api/lending/edge/email_availability/?email=${encodedEmail}`,
    {
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        authorization: API_AUTH_TOKEN,
      },
    },
  );

  if (!response.ok) {
    const text = await response.text();
    const error: any = new Error(text);
    error.status = response.status;
    error.response = { status: response.status };
    throw error;
  }

  return await response.json();
}

/**
 * Check if an email exists in the system
 * This helps determine whether to use Django magic link or Cognito magic link
 *
 * @param email - The email address to check
 * @returns Promise<boolean> - true if email exists in Django/legacy system, false if potentially new Cognito user
 *
 * Uses the existing email_availability endpoint which returns:
 * - { available: true } if email doesn't exist (can be used for new signup)
 * - { available: false } if email exists (user should login)
 */
export async function checkEmailExists(email: string): Promise<boolean> {
  try {
    const response = await getEmailAvailability(email);
    // If email is NOT available, it means it exists in the system
    return response.data.available === false;
  } catch (error: any) {
    logger.error("Error checking email existence", {
      error: error?.message || "Unknown error",
      status: error?.status || "unknown",
    });
    // On errors, default to assuming email exists (use Django flow as fallback)
    // This is safer as it won't break existing functionality
    return true;
  }
}

/**
 * Determine which authentication flow to use based on email.
 *
 * Calls the auth-service's GET /auth-flow endpoint, which reads the user's
 * Cognito group membership directly: members of the `use_cognito` group are
 * routed to Cognito; everyone else (including users not in Cognito at all)
 * is routed to Django.
 *
 * On error we default to Django, the legacy flow that still works for every
 * customer — the Cognito flow only works for users in `use_cognito`, so
 * defaulting to it would lock out un-migrated users if auth-service is
 * unreachable.
 *
 * @param email - The email address to check
 * @returns Promise<'django' | 'auth-service'> - Which flow to use
 */
export async function determineAuthFlow(
  email: string,
): Promise<"django" | "auth-service"> {
  try {
    const response = await getAuthFlow(email);
    const flow = response.flow === "cognito" ? "auth-service" : "django";
    logger.info("Determining auth flow", { flowType: flow });
    return flow;
  } catch (error: any) {
    logger.error("Auth flow determination failed, defaulting to django", {
      error: error?.message || "Unknown error",
    });
    return "django";
  }
}
